import { sortedIndexBy } from 'lodash-es';
import moment from 'moment-timezone';

import { Form } from '@site-mate/dashpivot-shared-library';

import { TimelineGroup } from './timeline-group.model';
import { FormWeb } from '../../../shared/model/form.model';

/**
 * Timeline is a class used for managing a set of forms grouped by a given value
 */
export class Timeline {
  private groupBy: string;
  private groupsMap: Map<number, TimelineGroup> = new Map<number, TimelineGroup>();
  private forms: Map<string, FormWeb> = new Map<string, FormWeb>();
  groups: TimelineGroup[] = [];

  constructor(groupBy) {
    this.groupBy = groupBy || 'createdAt';
  }

  /**
   * This method initializes all the timeline groups
   */
  addFormsToTimeline(forms: FormWeb[], addInOrder = false) {
    forms.forEach((form) => {
      const key = this.calculateFormTimestampStartOfDay(form);
      let group = this.groupsMap.get(key);
      if (!group) {
        group = new TimelineGroup(key);
        this.insertGroup(group);
      }

      if (addInOrder) {
        group.pushFormInOrder(form, this.calculateFormTimestamp.bind(this));
      } else {
        group.pushForm(form);
      }

      this.forms.set(form._id, form);
    });
  }

  /**
   * This method inserts a given group to the groups array so that the array
   * preserves sorted order. Also it updates the groupsMap to include the newly
   * addded group in map for fast reference
   *
   * @param group a group to be inserted to the groups array
   */
  private insertGroup(group: TimelineGroup) {
    const insertionIndex = sortedIndexBy(this.groups, group, (g) => -g.timestamp);
    this.groups.splice(insertionIndex, 0, group);
    this.groupsMap.set(group.timestamp, group);
  }

  /**
   * This method returns a grouping key (timestamp) for the given form.
   * The timestamps are rounded down to the beginning of the day
   *
   * NOTE: date of creation is used as a fallback date in case the
   *       required field is not set on the form
   *
   * @param form a form whose group timestamp is to be determined
   * @param groupBy the name of a date field used for grouping
   */
  private calculateFormTimestampStartOfDay(form) {
    return +moment(this.getSortDate(form)).startOf('day');
  }

  private calculateFormTimestamp(form) {
    return +moment(this.getSortDate(form));
  }

  private getSortDate(form) {
    let date;

    if (this.groupBy === 'createdAt') {
      date = form.createdAt;
    } else {
      const item = form.items.find((i) => i._id === this.groupBy);
      date = item && item.startDate ? item.startDate : form.createdAt;
    }

    return date;
  }

  removeFormFromGroup(formId: string) {
    const timelineGroup = this.getFormGroup(formId);
    timelineGroup.removeForm(formId);
    this.forms.delete(formId);
  }

  updateFormInGroup(updatedForms: FormWeb[]) {
    const updatedForm = updatedForms[0];
    const formToUpdate = this.forms.get(updatedForm._id);

    if (this.areFormsInSameGroup(formToUpdate, updatedForm)) {
      const groupWithFormToUpdate = this.getFormGroup(formToUpdate._id);
      groupWithFormToUpdate.updateForm(updatedForm, this.calculateFormTimestamp.bind(this));
      this.forms.set(formToUpdate._id, updatedForm);
    } else {
      this.removeFormFromGroup(formToUpdate._id);
      this.addFormsToTimeline([updatedForm], true);
    }
  }

  setFormInUpdatingState(formId: string) {
    if (this.forms.get(formId)) {
      this.forms.get(formId).updating = true;
    }
  }

  private areFormsInSameGroup(form: Form, formToCompare: Form) {
    return (
      this.calculateFormTimestampStartOfDay(form) === this.calculateFormTimestampStartOfDay(formToCompare)
    );
  }

  private getFormGroup(formId: string): TimelineGroup {
    const form = this.forms.get(formId);
    return this.groupsMap.get(this.calculateFormTimestampStartOfDay(form));
  }

  get formsSize(): number {
    return this.forms.size;
  }
}
